import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import ConfirmModal from './ConfirmModal';
import { DEFAULT_DEPTH_BAND_EDGES, depthBandLabel, bandEdgeErrors } from '../utils/depthBands';

// Settings card: Snow depth pricing (design 4.2). Owner and admin only, like
// the tier list it sits under. Everything is saved through PUT
// /api/depth-pricing; the server repeats every check made here.
//
// Prices are held by band POSITION, not band id, because a band added here has
// no id until it is saved, and the server takes prices by position too.

const API = process.env.REACT_APP_API_URL;
const X_LARGE = /^(x[-\s]?large|extra[-\s]?large|xl)$/i;
const ENABLE_MESSAGE = 'New snow routes will be priced by measured depth. Existing routes and completed stops keep their prices.';
const DISABLE_MESSAGE = 'New snow routes will use the flat price again. Routes already created by depth keep being priced by depth.';

const edgeText = (v) => (v === null || v === undefined ? null : String(parseFloat(v)));
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

function DepthPricingCard({ tiersVersion, onTiersChanged }) {
  const [server, setServer] = useState(null);
  const [edges, setEdges] = useState([]);
  const [cells, setCells] = useState({}); // { [tierId]: { [position]: '65.00' } }
  const [premium, setPremium] = useState('20');
  const [source, setSource] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirm, setConfirm] = useState(null); // 'enable' | 'disable' | 'removeBands'
  const [addingXL, setAddingXL] = useState(false);

  const applyState = useCallback((state, keepEdits) => {
    setServer(state);
    const positionOf = new Map(state.bands.map((b) => [b.id, b.position]));
    const fromServer = {};
    state.tiers.forEach((t) => {
      fromServer[t.id] = {};
      Object.entries(t.prices).forEach(([bandId, price]) => {
        fromServer[t.id][positionOf.get(Number(bandId))] = Number(price).toFixed(2);
      });
    });
    if (keepEdits) {
      // A tier was added: keep what the owner had typed, add the new row.
      setCells((prev) => {
        const next = { ...fromServer };
        Object.keys(prev).forEach((id) => { if (next[id]) next[id] = prev[id]; });
        return next;
      });
      return;
    }
    setEdges(state.bands.map((b) => edgeText(b.upperInches)));
    setCells(fromServer);
    setPremium(String(state.onCallPremiumPercent));
    setSource(state.sourceDefault || '');
    setDirty(false);
  }, []);

  const load = useCallback(async (keepEdits = false) => {
    try {
      const res = await axios.get(`${API}/api/depth-pricing`);
      applyState(res.data, keepEdits);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load depth pricing');
    }
  }, [applyState]);

  // tiersVersion changes whenever the tier list above is reloaded, so a tier
  // renamed, added or retired there shows up here without a page refresh.
  useEffect(() => { load(dirty); }, [tiersVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!server) {
    return (
      <div className="card mb-lg">
        <div className="card-header"><h2 className="card-title">Snow depth pricing</h2></div>
        {error ? <div className="alert alert-error">{error}</div> : <p className="text-muted">Loading...</p>}
      </div>
    );
  }

  const bands = edges.map((upperInches, i) => ({ position: i + 1, upperInches }));
  const labels = bands.map((_, i) => depthBandLabel(bands, i));
  const edgeErrors = bandEdgeErrors(edges);
  const touch = () => { setDirty(true); setSuccess(''); setError(''); };

  const blankCount = server.tiers.reduce((n, t) => n + bands.filter((b) => {
    const v = cells[t.id]?.[b.position];
    return v === undefined || v === '';
  }).length, 0);
  const localComplete = server.tiers.length > 0 && blankCount === 0 && edgeErrors.length === 0;
  const hasXLarge = server.tiers.some((t) => X_LARGE.test(t.name.trim()));
  const withoutTier = server.customersWithoutTierCount;

  const setEdge = (i, value) => { touch(); setEdges((prev) => prev.map((e, j) => (j === i ? value : e))); };
  const setCell = (tierId, position, value) => {
    touch();
    setCells((prev) => ({ ...prev, [tierId]: { ...(prev[tierId] || {}), [position]: value } }));
  };

  const addBand = () => {
    touch();
    setEdges((prev) => {
      const closed = prev.slice(0, -1).map((e) => parseFloat(e)).filter(Number.isFinite);
      const top = closed.length ? Math.max(...closed) : 0;
      return [...prev.slice(0, -1), String(top + 3), null];
    });
  };
  const removeLastBand = () => {
    if (edges.length <= 1) return;
    touch();
    setEdges((prev) => [...prev.slice(0, -2), null]);
  };
  const resetBands = () => { touch(); setEdges(DEFAULT_DEPTH_BAND_EDGES.map(edgeText)); };

  const fillFromFlat = () => {
    touch();
    setCells((prev) => {
      const next = { ...prev };
      server.tiers.forEach((t) => {
        const row = { ...(next[t.id] || {}) };
        if (row[1] === undefined || row[1] === '') row[1] = Number(t.defaultPrice).toFixed(2);
        next[t.id] = row;
      });
      return next;
    });
  };

  const addXLarge = async () => {
    setAddingXL(true);
    setError('');
    try {
      const all = await axios.get(`${API}/api/pricing-tiers/all?product=snow`);
      const nextOrder = all.data.reduce((m, t) => Math.max(m, Number(t.sort_order) || 0), 0) + 1;
      // The starter values migration 062 and registration seed.
      await axios.post(`${API}/api/pricing-tiers`, {
        name: 'X-Large', default_price: 110, salt_price: 25, sort_order: nextOrder, product_type: 'snow',
      });
      setSuccess('X-Large added. Fill in its depth prices.');
      if (onTiersChanged) onTiersChanged();
      await load(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add X-Large');
    } finally {
      setAddingXL(false);
    }
  };

  const put = async (body, okMessage) => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await axios.put(`${API}/api/depth-pricing`, body);
      applyState(res.data, false);
      setSuccess(okMessage);
    } catch (err) {
      const data = err.response?.data || {};
      if (data.code === 'CUSTOMERS_WITHOUT_TIER') {
        setError(`${data.count} active customer(s) have no snow tier. Give each one a tier before enabling.`);
        load(dirty);
      } else if (data.errors && data.errors.length) {
        setError(data.errors.join(' '));
      } else {
        setError(data.error || 'Failed to save depth pricing');
      }
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    const prices = [];
    server.tiers.forEach((t) => bands.forEach((b) => {
      const v = cells[t.id]?.[b.position];
      prices.push({ tierId: t.id, bandPosition: b.position, price: v === undefined || v === '' ? null : Number(v) });
    }));
    put({
      bands: edges.map((e) => ({ upperInches: e === null || e === '' ? null : Number(e) })),
      prices,
      onCallPremiumPercent: premium === '' ? null : Number(premium),
      sourceDefault: source,
    }, 'Depth pricing saved');
  };

  const removedBandsHavePrices = edges.length < server.bands.length && server.tiers.some(
    (t) => server.bands.some((b) => b.position > edges.length && t.prices[b.id] !== undefined)
  );
  const onSave = () => (removedBandsHavePrices ? setConfirm('removeBands') : save());

  const toggleBlockedReason = server.enabled ? null
    : dirty ? 'Save your changes first.'
      : !server.complete ? 'Fill every price to enable.'
        : withoutTier > 0 ? 'Every active customer needs a snow tier first.'
          : null;

  return (
    <div className="card mb-lg depth-card">
      <div className="card-header">
        <h2 className="card-title">Snow depth pricing</h2>
        <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: '4px' }}>
          Price each storm by the measured snow depth instead of one flat price per size.
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <h3 className="depth-subtitle">Depth bands</h3>
      <div className="depth-edges" aria-label="Band edges in inches">
        <span className="depth-edge-fixed">0</span>
        {edges.slice(0, -1).map((e, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={i}>
            <span className="depth-edge-dash">&ndash;</span>
            <input
              type="number"
              className="form-input depth-edge-input"
              value={e ?? ''}
              onChange={(ev) => setEdge(i, ev.target.value)}
              step="0.1"
              min="0.1"
              aria-label={`Band ${i + 1} upper edge (inches)`}
            />
          </React.Fragment>
        ))}
        <span className="depth-edge-dash">&ndash;</span>
        <span className="depth-edge-fixed">+</span>
        <span className="text-muted depth-edge-unit">inches</span>
      </div>
      {edgeErrors.length > 0 && <div className="alert alert-error">{edgeErrors.join(' ')}</div>}
      <div className="depth-band-actions">
        <button type="button" className="btn btn-sm btn-outline" onClick={addBand} disabled={edges.length >= 10}>+ Add band</button>
        <button type="button" className="btn btn-sm btn-outline" onClick={removeLastBand} disabled={edges.length <= 1}>Remove last band</button>
        <button type="button" className="btn btn-sm btn-outline" onClick={resetBands}>Reset to defaults</button>
      </div>

      <h3 className="depth-subtitle">Prices by size and depth</h3>
      {server.tiers.length === 0 ? (
        <p className="text-muted">Add a snow pricing tier above first.</p>
      ) : (
        <div className="depth-grid-wrap">
          <table className="depth-grid">
            <thead>
              <tr>
                <th scope="col">Size</th>
                {labels.map((l) => <th key={l} scope="col">{l}</th>)}
                <th scope="col" className="depth-salt-col" title="Edited in the tier list above">Salt</th>
              </tr>
            </thead>
            <tbody>
              {server.tiers.map((t) => (
                <tr key={t.id}>
                  <th scope="row">
                    {t.name}
                    {!t.isActive && <span className="depth-inactive"> (inactive)</span>}
                  </th>
                  {bands.map((b) => {
                    const v = cells[t.id]?.[b.position] ?? '';
                    return (
                      <td key={b.position}>
                        <div className={`depth-cell ${v === '' ? 'depth-cell-blank' : ''}`}>
                          <span>$</span>
                          <input
                            type="number"
                            value={v}
                            onChange={(e) => setCell(t.id, b.position, e.target.value)}
                            step="0.01"
                            min="0"
                            aria-label={`${t.name}, ${labels[b.position - 1]}`}
                          />
                        </div>
                      </td>
                    );
                  })}
                  <td className="depth-salt-col">{money(t.saltPrice)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {blankCount > 0 && server.tiers.length > 0 && (
        <p className="depth-blank-note">{blankCount} empty price{blankCount === 1 ? '' : 's'} (highlighted).</p>
      )}
      <div className="depth-band-actions">
        <button type="button" className="btn btn-sm btn-outline" onClick={fillFromFlat} disabled={server.tiers.length === 0}>
          Fill from flat price
        </button>
        {!hasXLarge && (
          <button type="button" className="btn btn-sm btn-outline" onClick={addXLarge} disabled={addingXL}>
            {addingXL ? 'Adding...' : '+ Add X-Large'}
          </button>
        )}
      </div>
      <small className="text-muted">Salt stays one flat price per size and is edited in the tier list above.</small>

      <div className="depth-fields">
        <div className="form-group">
          <label className="form-label" htmlFor="depth-premium">On-call premium</label>
          <div className="input-with-suffix">
            <input
              id="depth-premium"
              type="number"
              className="form-input"
              value={premium}
              onChange={(e) => { touch(); setPremium(e.target.value); }}
              step="0.01"
              min="0"
              max="100"
            />
            <span className="input-suffix">%</span>
          </div>
          <small className="text-muted">
            Added to snow, salt and add-ons, before discounts, for customers set to On-call. It applies
            only on routes priced by depth. Flat-priced routes never add it.
            {' '}{server.onCallCustomerCount} on-call &middot; {server.seasonalCustomerCount} seasonal customers
          </small>
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="depth-source">Depth source</label>
          <input
            id="depth-source"
            type="text"
            className="form-input"
            value={source}
            onChange={(e) => { touch(); setSource(e.target.value); }}
            maxLength={120}
            placeholder="NWS Green Bay storm total, Appleton"
          />
          <small className="text-muted">Where you take storm totals from. Filled in each time you enter a depth.</small>
        </div>
      </div>

      <div className="depth-save-row">
        <button
          type="button"
          className="btn btn-primary"
          onClick={onSave}
          disabled={saving || !dirty || edgeErrors.length > 0}
        >
          {saving ? 'Saving...' : 'Save depth pricing'}
        </button>
        {dirty && !localComplete && server.tiers.length > 0 && edgeErrors.length === 0 && (
          <span className="text-muted">You can save an unfinished matrix and come back to it.</span>
        )}
      </div>

      <div className="depth-enable">
        <label className={`depth-enable-toggle ${toggleBlockedReason ? 'disabled' : ''}`}>
          <input
            type="checkbox"
            checked={server.enabled}
            disabled={saving || !!toggleBlockedReason}
            onChange={() => setConfirm(server.enabled ? 'disable' : 'enable')}
          />
          <span>Price new snow routes by depth</span>
        </label>
        <span className={`depth-status ${server.enabled ? 'on' : 'off'}`}>{server.enabled ? 'On' : 'Off'}</span>
        {toggleBlockedReason && <p className="text-muted depth-enable-reason">{toggleBlockedReason}</p>}
        {withoutTier > 0 && (
          <p className="depth-without-tier">
            {withoutTier} active customer{withoutTier === 1 ? ' has' : 's have'} no snow tier.
            {' '}Every customer on a depth route needs one; set it on the Customers page.
          </p>
        )}
      </div>

      <ConfirmModal
        show={confirm === 'enable'}
        title="Turn on depth pricing?"
        message={ENABLE_MESSAGE}
        confirmText="Turn on"
        onCancel={() => setConfirm(null)}
        onConfirm={() => { setConfirm(null); put({ enabled: true }, 'Depth pricing is on for new snow routes'); }}
      />
      <ConfirmModal
        show={confirm === 'disable'}
        title="Turn off depth pricing?"
        message={DISABLE_MESSAGE}
        confirmText="Turn off"
        onCancel={() => setConfirm(null)}
        onConfirm={() => { setConfirm(null); put({ enabled: false }, 'Depth pricing is off for new snow routes'); }}
      />
      <ConfirmModal
        show={confirm === 'removeBands'}
        title="Remove bands?"
        message="The bands you removed have prices. Saving deletes those prices."
        confirmText="Remove and save"
        confirmColor="#dc2626"
        onCancel={() => setConfirm(null)}
        onConfirm={() => { setConfirm(null); save(); }}
      />

      <style>{`
        .depth-subtitle { font-size: 0.95rem; font-weight: 600; margin: 16px 0 8px; }
        .depth-edges { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
        .depth-edge-fixed { font-weight: 600; min-width: 12px; text-align: center; }
        .depth-edge-dash { color: #9ca3af; }
        .depth-edge-input { width: 64px !important; padding: 6px 8px !important; }
        .depth-edge-unit { font-size: 0.8rem; margin-left: 4px; }
        .depth-band-actions { display: flex; flex-wrap: wrap; gap: 8px; margin: 10px 0; }
        .depth-grid-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; border: 1px solid #e5e7eb; border-radius: 8px; }
        .depth-grid { border-collapse: collapse; width: 100%; font-size: 0.875rem; }
        .depth-grid th, .depth-grid td { padding: 6px 8px; border-bottom: 1px solid #e5e7eb; text-align: left; white-space: nowrap; }
        .depth-grid thead th { background: #f9fafb; font-size: 0.8rem; color: #6b7280; font-weight: 600; }
        .depth-grid tbody tr:last-child th, .depth-grid tbody tr:last-child td { border-bottom: none; }
        .depth-grid tbody th { font-weight: 600; position: sticky; left: 0; background: #fff; z-index: 1; }
        .depth-cell { display: flex; align-items: center; gap: 2px; border: 1px solid #d1d5db; border-radius: 6px; padding: 0 6px; background: #fff; }
        .depth-cell input { width: 64px; border: none; outline: none; padding: 6px 0; background: transparent; font-size: 0.875rem; }
        .depth-cell-blank { background: #fef3c7; border-color: #f59e0b; }
        .depth-salt-col { color: #6b7280; background: #f9fafb; }
        .depth-inactive { font-weight: 400; color: #9ca3af; font-size: 0.8rem; }
        .depth-blank-note { color: #b45309; font-size: 0.8125rem; margin: 6px 0 0; }
        .depth-fields { display: grid; grid-template-columns: 1fr 2fr; gap: 16px; margin-top: 16px; }
        .depth-fields .form-group small { display: block; margin-top: 4px; }
        .input-with-suffix { display: flex; align-items: center; gap: 6px; max-width: 140px; }
        .depth-save-row { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-top: 8px; }
        .depth-save-row .text-muted { font-size: 0.8125rem; }
        .depth-enable { margin-top: 20px; padding: 14px 16px; border: 1px solid #e5e7eb; border-radius: 8px; background: #f9fafb; }
        .depth-enable-toggle { display: inline-flex; align-items: center; gap: 8px; font-weight: 600; cursor: pointer; }
        .depth-enable-toggle.disabled { cursor: not-allowed; color: #9ca3af; }
        .depth-enable-toggle input { width: 18px; height: 18px; }
        .depth-status { margin-left: 10px; font-size: 0.75rem; font-weight: 700; padding: 2px 8px; border-radius: 999px; }
        .depth-status.on { background: #d1fae5; color: #065f46; }
        .depth-status.off { background: #e5e7eb; color: #374151; }
        .depth-enable-reason { margin: 6px 0 0; font-size: 0.8125rem; }
        .depth-without-tier { margin: 6px 0 0; font-size: 0.8125rem; color: #b45309; }
        @media (max-width: 640px) {
          .depth-fields { grid-template-columns: 1fr; }
          .depth-edges { gap: 3px; }
          .depth-edge-input { width: 50px !important; padding: 6px 4px !important; }
        }
      `}</style>
    </div>
  );
}

export default DepthPricingCard;
